import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Layout from "./components/Layout.js";
import ProtectedRoute from "./components/ProtectedRoute.js";
import RouteLoading from "./components/RouteLoading.js";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary.js";
import { useAuth } from "./auth/AuthContext.js";

const StatsPage = lazy(() => import("./pages/StatsPage.js"));
const CatchDetailPage = lazy(() => import("./pages/CatchDetailPage.js"));
const CatchFormPage = lazy(() => import("./pages/CatchFormPage.js"));
const MyCatchesPage = lazy(() => import("./pages/MyCatchesPage.js"));
const ProfilePage = lazy(() => import("./pages/ProfilePage.js"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage.js"));
const CatchHistoryPage = lazy(() => import("./pages/CatchHistoryPage.js"));
const ValdPage = lazy(() => import("./pages/ValdPage.js"));
const FiskeDiscoveryPage = lazy(() => import("./pages/FiskeDiscoveryPage.js"));
const RiversPage = lazy(() => import("./pages/RiversPage.js"));
const BliValdeierPage = lazy(() => import("./pages/BliValdeierPage.js"));
const ValdKomIGangPage = lazy(() => import("./pages/ValdKomIGangPage.js"));
const FiskekortPurchasePage = lazy(
  () => import("./pages/FiskekortPurchasePage.js"),
);
const MyFiskekortPage = lazy(() => import("./pages/MyFiskekortPage.js"));
const MyBookingsPage = lazy(() => import("./pages/MyBookingsPage.js"));
const ValdAdminDashboardPage = lazy(
  () => import("./pages/ValdAdminDashboardPage.js"),
);
const ValdAdminValdPage = lazy(() => import("./pages/ValdAdminValdPage.js"));
const ValdAdminRevenuePage = lazy(
  () => import("./pages/ValdAdminRevenuePage.js"),
);
const RiverAdminDashboardPage = lazy(
  () => import("./pages/RiverAdminDashboardPage.js"),
);
const BookAccommodationPage = lazy(
  () => import("./pages/BookAccommodationPage.js"),
);
const BulkSmåfiskPage = lazy(() => import("./pages/BulkSmåfiskPage.js"));
const LoggFiskedagPage = lazy(() => import("./pages/LoggFiskedagPage.js"));
const EditCatchPage = lazy(() => import("./pages/EditCatchPage.js"));
const FiskekortPaymentReturnPage = lazy(
  () => import("./pages/FiskekortPaymentReturnPage.js"),
);
const FiskekortBevisPage = lazy(() => import("./pages/FiskekortBevisPage.js"));
const AccommodationPaymentReturnPage = lazy(
  () => import("./pages/AccommodationPaymentReturnPage.js"),
);
const PackageOrderPage = lazy(() => import("./pages/PackageOrderPage.js"));
const PackageOrderReturnPage = lazy(
  () => import("./pages/PackageOrderReturnPage.js"),
);
const ContactPage = lazy(() => import("./pages/ContactPage.js"));
const ElvekortPurchasePage = lazy(
  () => import("./pages/ElvekortPurchasePage.js"),
);
const ElvekortPaymentReturnPage = lazy(
  () => import("./pages/ElvekortPaymentReturnPage.js"),
);
const RiverPage = lazy(() => import("./pages/RiverPage.js"));
const ElveeierlagPage = lazy(() => import("./pages/ElveeierlagPage.js"));
const SelgFiskekortPage = lazy(() => import("./pages/SelgFiskekortPage.js"));
const DesignSystemPage = lazy(() => import("./pages/DesignSystemPage.js"));
const PhotoContestPage = lazy(() => import("./pages/PhotoContestPage.js"));
const AdminContestsPage = lazy(() => import("./pages/AdminContestsPage.js"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage.js"));

const DEFAULT_TITLE =
  "Lakseborsen — Fangstregistrering og statistikk for norske lakseelver";

export default function App() {
  const { pathname } = useLocation();
  // A page entered via a server-rendered route loses its server-written title
  // when the app takes over (see PageMeta). Pages without their own PageMeta
  // would then have no title at all, so fall back to the site title.
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (!document.title) document.title = DEFAULT_TITLE;
    }, 0);
    return () => window.clearTimeout(id);
  }, [pathname]);
  const { isAdmin, isValdOwner, isRiverAdmin } = useAuth();
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<StatsPage />} />
            <Route path="/historikk" element={<CatchHistoryPage />} />
            <Route path="/kontakt" element={<ContactPage />} />
            <Route
              path="/elvekort/:productId/kjop"
              element={
                <ProtectedRoute>
                  <ElvekortPurchasePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/elvekort/payment/return"
              element={
                <ProtectedRoute>
                  <ElvekortPaymentReturnPage />
                </ProtectedRoute>
              }
            />
            <Route path="/design-system" element={<DesignSystemPage />} />
            <Route path="/fotokonkurranse" element={<PhotoContestPage />} />
            <Route path="/elveeierlag" element={<ElveeierlagPage />} />
            <Route path="/selg-fiskekort" element={<SelgFiskekortPage />} />
            <Route path="/fangst/:id" element={<CatchDetailPage />} />
            <Route
              path="/fangst/:id/rediger"
              element={
                <ProtectedRoute reason="auth.loginForCatch">
                  <EditCatchPage />
                </ProtectedRoute>
              }
            />
            {/* Public vald promo pages */}
            <Route path="/elver" element={<RiversPage />} />
            <Route path="/fiskekort" element={<FiskeDiscoveryPage />} />
            <Route path="/bli-valdeier" element={<BliValdeierPage />} />
            <Route path="/river/:riverId" element={<RiverPage />} />
            <Route path="/elv/:riverId" element={<RiverPage />} />
            <Route path="/vald/:valdId" element={<ValdPage />} />
            <Route
              path="/vald/:valdId/fiskekort/:productId/kjop"
              element={
                <ProtectedRoute>
                  <FiskekortPurchasePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vald/:valdId/overnatting/:listingId/book"
              element={
                <ProtectedRoute>
                  <BookAccommodationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fangst/ny"
              element={
                <ProtectedRoute reason="auth.loginForCatch">
                  <CatchFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fangst/bulk"
              element={
                <ProtectedRoute reason="auth.loginForCatch">
                  <BulkSmåfiskPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/logg-fiskedag"
              element={
                <ProtectedRoute reason="auth.loginForCatch">
                  <LoggFiskedagPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fangst"
              element={
                <ProtectedRoute reason="auth.loginForMyCatches">
                  <MyCatchesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mine/fiskekort"
              element={
                <ProtectedRoute>
                  <MyFiskekortPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mine/fiskekort/:purchaseId/bevis"
              element={
                <ProtectedRoute>
                  <FiskekortBevisPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mine/bookinger"
              element={
                <ProtectedRoute>
                  <MyBookingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fiskekort/payment/return"
              element={
                <ProtectedRoute>
                  <FiskekortPaymentReturnPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vald/:valdId/accommodation/payment/return"
              element={
                <ProtectedRoute>
                  <AccommodationPaymentReturnPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/valds/:valdId/packages/:packageId/order"
              element={
                <ProtectedRoute>
                  <PackageOrderPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/package-orders/:id/return"
              element={
                <ProtectedRoute>
                  <PackageOrderReturnPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profil"
              element={
                <ProtectedRoute reason="auth.loginForProfile">
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            {isAdmin && (
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboardPage />
                  </ProtectedRoute>
                }
              />
            )}
            {isAdmin && (
              <Route
                path="/admin/fotokonkurranse"
                element={
                  <ProtectedRoute>
                    <AdminContestsPage />
                  </ProtectedRoute>
                }
              />
            )}
            {(isRiverAdmin || isAdmin) && (
              <Route
                path="/river-admin"
                element={
                  <ProtectedRoute>
                    <RiverAdminDashboardPage />
                  </ProtectedRoute>
                }
              />
            )}
            {isValdOwner && (
              <>
                <Route
                  path="/vald-admin"
                  element={
                    <ProtectedRoute>
                      <ValdAdminDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vald-admin/:valdId"
                  element={
                    <ProtectedRoute>
                      <ValdAdminValdPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vald-admin/:valdId/revenue"
                  element={
                    <ProtectedRoute>
                      <ValdAdminRevenuePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/vald-admin/komme-i-gang"
                  element={
                    <ProtectedRoute>
                      <ValdKomIGangPage />
                    </ProtectedRoute>
                  }
                />
              </>
            )}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </ChunkErrorBoundary>
  );
}
